import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { usePageTracking } from "@/hooks/usePageTracking";
import OfflineBanner from "@/components/OfflineBanner";

import Index from "./pages/Index";

const HinosAvulsos = lazy(() => import("./pages/HinosAvulsos"));
const HinoDetail = lazy(() => import("./pages/HinoDetail"));
const OuvirHinos = lazy(() => import("./pages/OuvirHinos"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Install = lazy(() => import("./pages/Install"));
const Afinador = lazy(() => import("./pages/Afinador"));
const LouveStore = lazy(() => import("./pages/LouveStore"));
const Perfil = lazy(() => import("./pages/Perfil"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AulaIA = lazy(() => import("./pages/AulaIA"));
const BaixarCifras = lazy(() => import("./pages/BaixarCifras"));
const MinhasCifras = lazy(() => import("./pages/MinhasCifras"));
const AprenderViolao = lazy(() => import("./pages/AprenderViolao"));
const DicionarioAcordes = lazy(() => import("./pages/DicionarioAcordes"));
const CampoHarmonico = lazy(() => import("./pages/CampoHarmonico"));
const GeradorCifra = lazy(() => import("./pages/GeradorCifra"));
const JogosMusicais = lazy(() => import("./pages/JogosMusicais"));
const JogoDesafioAcordes = lazy(() => import("./pages/JogoDesafioAcordes"));
const JogoCampoHarmonico = lazy(() => import("./pages/JogoCampoHarmonico"));
const JogoCompleteCifra = lazy(() => import("./pages/JogoCompleteCifra"));
const JogoAdivinheHino = lazy(() => import("./pages/JogoAdivinheHino"));
const JogoDesafio60s = lazy(() => import("./pages/JogoDesafio60s"));
const HinoProfissional = lazy(() => import("./pages/HinoProfissional"));
const WelcomeMessage = lazy(() => import("./components/WelcomeMessage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60 * 24,
      retry: (failureCount) => {
        if (typeof navigator !== "undefined" && !navigator.onLine) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const PageFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const AppRoutes = () => {
  usePageTracking();

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/hinos-avulsos" element={<HinosAvulsos />} />
        <Route path="/hino/:id" element={<HinoDetail />} />
        <Route path="/hino/:id/aula-ia" element={<AulaIA />} />
        <Route path="/ouvir-hinos" element={<OuvirHinos />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/instalar" element={<Install />} />
        <Route path="/afinador" element={<Afinador />} />
        <Route path="/louve-store" element={<LouveStore />} />
        <Route path="/aprender-violao" element={<AprenderViolao />} />
        <Route path="/dicionario-acordes" element={<DicionarioAcordes />} />
        <Route path="/campo-harmonico" element={<CampoHarmonico />} />
        <Route path="/gerador-cifra" element={<GeradorCifra />} />
        <Route path="/jogos-musicais" element={<JogosMusicais />} />
        <Route path="/jogos-musicais/desafio-acordes" element={<JogoDesafioAcordes />} />
        <Route path="/jogos-musicais/campo-harmonico" element={<JogoCampoHarmonico />} />
        <Route path="/jogos-musicais/complete-cifra" element={<JogoCompleteCifra />} />
        <Route path="/jogos-musicais/adivinhe-hino" element={<JogoAdivinheHino />} />
        <Route path="/jogos-musicais/desafio-60s" element={<JogoDesafio60s />} />
        <Route path="/hino-profissional" element={<HinoProfissional />} />
        <Route path="/perfil" element={<Perfil />} />
        {/* Redirect old routes to unified profile */}
        <Route path="/meus-pedidos" element={<Navigate to="/perfil?section=pedidos" replace />} />
        <Route path="/editar-perfil" element={<Navigate to="/perfil?section=editar" replace />} />
        <Route path="/configuracoes" element={<Navigate to="/perfil?section=configuracoes" replace />} />
        <Route path="/notificacoes" element={<Navigate to="/perfil?section=notificacoes" replace />} />
        <Route path="/idioma" element={<Navigate to="/perfil?section=idioma" replace />} />
        <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
        <Route path="/termos-de-uso" element={<TermosDeUso />} />
        <Route path="/baixar-cifras" element={<BaixarCifras />} />
        <Route path="/minhas-cifras" element={<MinhasCifras />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <OfflineBanner />
        <Suspense fallback={null}>
          <WelcomeMessage />
        </Suspense>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
